import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlay, FaRegClock, FaSpa, FaWind, FaCrown } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { getPractices, getTopMenuData } from "../../api/mainApi";
import Premium from "../premium/Premium";

const categoryConfig = [
  { key: "breathing", i18nKey: "meditation.breathing", Icon: FaWind },
  { key: "guided", i18nKey: "meditation.guided", Icon: FaSpa },
];

const THEME_STYLES = {
  blue: {
    bg: "bg-gradient-to-br from-blue-800 to-slate-900",
    border: "border-blue-700/50",
  },
  emerald: {
    bg: "bg-gradient-to-br from-emerald-800 to-teal-950",
    border: "border-emerald-700/50",
  },
  indigo: {
    bg: "bg-gradient-to-br from-indigo-800 to-slate-900",
    border: "border-indigo-700/50",
  },
  violet: {
    bg: "bg-gradient-to-br from-violet-800 to-purple-950",
    border: "border-violet-700/50",
  },
  sky: {
    bg: "bg-gradient-to-br from-sky-800 to-slate-900",
    border: "border-sky-700/50",
  },
  purple: {
    bg: "bg-gradient-to-br from-purple-800 to-indigo-950",
    border: "border-purple-700/50",
  },
  amber: {
    bg: "bg-gradient-to-br from-amber-700 to-amber-950",
    border: "border-amber-600/50",
  },
  orange: {
    bg: "bg-gradient-to-br from-orange-700 to-orange-950",
    border: "border-orange-600/50",
  },
  rose: {
    bg: "bg-gradient-to-br from-rose-700 to-rose-950",
    border: "border-rose-600/50",
  },
  warm: {
    bg: "bg-gradient-to-br from-yellow-700/80 to-amber-900",
    border: "border-yellow-600/50",
  },
  brown: {
    bg: "bg-gradient-to-br from-stone-700 to-stone-900",
    border: "border-stone-600/50",
  },
  default: {
    bg: "bg-gradient-to-br from-slate-800 to-slate-900",
    border: "border-slate-600/50",
  },
};

function MeditationList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [active, setActive] = useState("breathing");
  const [practices, setPractices] = useState({ guided: [], breathing: [] });
  const [loading, setLoading] = useState(true);

  // Преміум
  const [isUserPremium, setIsUserPremium] = useState(false);
  const [showPremium, setShowPremium] = useState(false);

  useEffect(() => {
    const fetchPractices = async () => {
      try {
        const data = await getPractices();
        setPractices(data);
        if (
          (!data.breathing || data.breathing.length === 0) &&
          data.guided?.length > 0
        ) {
          setActive("guided");
        }
      } catch (error) {
        console.error("Помилка завантаження практик:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchPremiumStatus = async () => {
      try {
        const apiData = await getTopMenuData();
        setIsUserPremium(apiData.premium > 0);
      } catch (error) {
        console.error("Помилка отримання статусу преміум:", error);
      }
    };

    fetchPractices();
    fetchPremiumStatus();
  }, []);

  const handleCardClick = (item) => {
    if (item.isPremium && !isUserPremium) {
      setShowPremium(true);
      return;
    }
    navigate(`/meditation-player/${item.id}`, {
      state: { practiceData: item },
    });
  };

  const currentList = practices[active] || [];
  const availableCategories = categoryConfig.filter(
    (c) => practices[c.key] && practices[c.key].length > 0,
  );

  return (
    <div className="select-none">
      {/* HEADER */}
      <div
        className={`flex items-center ${availableCategories.length > 1 ? "min-h-[36px] mb-4" : "mb-2"}`}
      >
        {loading ? (
          <div className="flex gap-2">
            <div className="w-24 h-9 bg-white/10 rounded-full animate-pulse" />
            <div className="w-28 h-9 bg-white/10 rounded-full animate-pulse" />
          </div>
        ) : availableCategories.length > 1 ? (
          <div className="flex justify-center gap-2 w-full">
            {availableCategories.map(({ key, i18nKey, Icon }) => (
              <button
                key={key}
                onClick={() => setActive(key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  active === key
                    ? "bg-white/15 text-white"
                    : "text-white/35 hover:text-white/60"
                }`}
              >
                <Icon className="text-sm shrink-0" />
                {t(i18nKey)}
              </button>
            ))}
          </div>
        ) : availableCategories.length === 1 ? (
          availableCategories.map(({ key, i18nKey, Icon }) => (
            <div key={key} className="flex items-center gap-2 font-bold w-full">
              <Icon className="text-emerald-400 w-4 h-4 shrink-0" />
              <p>{t(i18nKey)}</p>
            </div>
          ))
        ) : null}
      </div>

      {/* Карусель */}
      <div className="-mx-4 overflow-hidden relative">
        <div
          className="flex gap-3 overflow-x-auto px-4 pb-2 hide-scrollbar"
          style={{
            WebkitMaskImage:
              "linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)",
            maskImage:
              "linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)",
          }}
        >
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-36 h-36 rounded-2xl bg-white/5 border-2 border-white/5 relative overflow-hidden flex flex-col justify-between p-3 animate-pulse"
              >
                <div className="w-5 h-5 bg-white/10 rounded-full" />
                <div>
                  <div className="h-4 bg-white/10 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-white/10 rounded w-1/2" />
                </div>
              </div>
            ))
          ) : currentList.length > 0 ? (
            currentList.map((item) => {
              const theme = THEME_STYLES[item.color] || THEME_STYLES.default;
              return (
                <div
                  key={item.id}
                  onClick={() => handleCardClick(item)}
                  className={`
                    flex-shrink-0 w-36 h-36 rounded-2xl cursor-pointer
                    ${theme.bg} border-2 ${theme.border}
                    relative overflow-hidden
                    transition-transform hover:scale-[1.03]
                    flex flex-col justify-between p-3
                  `}
                >
                  {item.image && (
                    <div
                      className="absolute inset-0 bg-cover bg-center opacity-50"
                      style={{ backgroundImage: `url(${item.image})` }}
                    />
                  )}
                  {item.image && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  )}
                  {item.isPremium ? (
                    <FaCrown className="relative z-10 text-amber-500 text-sm self-start drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" />
                  ) : (
                    <div />
                  )}
                  <div className="relative z-10">
                    <p className="text-sm font-semibold text-white leading-tight mb-1 text-start drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                      {item.title}
                    </p>
                    <div className="flex items-center gap-1 text-[11px] text-white/80 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                      <FaRegClock className="flex-shrink-0" />
                      <span>
                        {item.duration || item.totalDurationMinutes}{" "}
                        {t("meditation.min")}
                      </span>
                    </div>
                  </div>
                  <div className="absolute top-2.5 right-2.5 z-10 bg-white/20 p-1.5 rounded-full backdrop-blur-md">
                    <FaPlay className="text-xs text-white" />
                  </div>
                </div>
              );
            })
          ) : (
            <div className="w-full text-center py-10 text-white/50 text-sm">
              {t("meditation.empty")}
            </div>
          )}
        </div>
      </div>

      {/* МОДАЛЬНЕ ВІКНО ПРЕМІУМ */}
      {showPremium && (
        <div className="fixed inset-0 z-[200]">
          <Premium showPremium={showPremium} setShowPremium={setShowPremium} />
        </div>
      )}

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}

export default MeditationList;
